import { useEffect, useState } from 'react';
import { Outlet } from 'react-router';
import DelayedSpinner from '../shared/DelayedSpinner';
import { AppToaster } from '../shared/toaster';
import { authFetch } from '../util/api';
import { EulaAcceptanceDialog } from './EulaAcceptanceDialog';
import { signOut, useReloadUser, useUserState } from './UserState';

/**
 * Component that checks if the user has accepted the EULA.
 * If not, shows a blocking dialog requiring acceptance before access is granted.
 */
export default function RequireEula() {
  const { authClaims, status, user } = useUserState();
  const reloadUser = useReloadUser();
  const [showEulaDialog, setShowEulaDialog] = useState(false);
  const [isCheckingEula, setIsCheckingEula] = useState(true);

  useEffect(() => {
    if (status === 'signedIn' && authClaims && user) {
      // Check if user has accepted EULA (stored in custom claims)
      const hasAcceptedEula = authClaims.eulaAcceptedAt != null;

      if (!hasAcceptedEula) {
        setShowEulaDialog(true);
      }
      setIsCheckingEula(false);
    }
  }, [status, authClaims, user]);

  const handleAcceptEula = async () => {
    try {
      // Call backend to record EULA acceptance
      await authFetch('/accept_eula', {
        method: 'POST',
        body: JSON.stringify({
          acceptedAt: Date.now(),
        }),
      });

      // Reload user to get updated custom claims
      await reloadUser();

      setShowEulaDialog(false);
    } catch (error) {
      console.error('Error accepting EULA:', error);
      AppToaster.show({
        message: 'Failed to accept EULA. Please try again.',
        intent: 'danger',
      });
      throw error; // Re-throw to keep dialog open
    }
  };

  const handleRejectEula = async () => {
    AppToaster.show({
      message: 'You must accept the EULA to use this platform.',
      intent: 'warning',
    });
    // Sign out the user if they reject the EULA
    await signOut();
  };

  if (status === 'loading' || isCheckingEula) {
    return <DelayedSpinner className="my-8" />;
  }

  return (
    <>
      <EulaAcceptanceDialog
        isOpen={showEulaDialog}
        onAccept={handleAcceptEula}
        onReject={handleRejectEula}
      />
      {!showEulaDialog && <Outlet />}
    </>
  );
}
